import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsersStore } from 'src/store/users';
import { useAuthStore } from 'src/store/auth';
import ConfirmPopup from 'src/components/ConfirmPopup';
import { RoleType, StatusActiveUser } from 'src/constants';
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from 'src/components/ui/pagination';

export const AdminStatus = {
  Pending: 0,
  Active: 1,
  Banned: -999,
}

const Admins = () => {
  const navigate = useNavigate();
  const { items, loading, error, loadList, remove, search, setSearch, usersOnly, adminsOnly } = useUsersStore();
  const { user: currentUser } = useAuthStore();

  // Check if user is admin, redirect to root if not
  useEffect(() => {
    if (currentUser && currentUser.role !== RoleType.Admin) {
      navigate('/');
    }
  }, [currentUser, navigate]);
  const [confirmOpen, setConfirmOpen] = useState<null | string | number>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{
    isOpen: boolean;
    userId: string | number;
    x: number;
    y: number;
  }>({
    isOpen: false,
    userId: '',
    x: 0,
    y: 0,
  });

  useEffect(() => {
    loadList();
  }, [loadList, search, usersOnly, adminsOnly]);


  const filtered = items.filter((u) => u.role >= 2); // only admins
  const totalPages = Math.ceil(filtered.length / itemsPerPage) || 1;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = filtered.slice(startIndex, startIndex + itemsPerPage);

  // Function to get role name from role value
  const getRoleName = (roleValue: number): string => {
    const roleEntry = Object.entries(RoleType).find(([_, value]) => value === roleValue);
    return roleEntry ? roleEntry[0] : 'Unknown';
  };

  // Function to check if user is current user
  const isCurrentUser = (userId: unknown): boolean => {
    return currentUser?.id === userId;
  };


  const handleContextMenuClick = (e: React.MouseEvent, userId: string | number) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      isOpen: true,
      userId,
      x: rect.right - 70,
      y: rect.bottom + 2,
    });
  };

  const handleCloseContextMenu = () => {
    setContextMenu({
      isOpen: false,
      userId: '',
      x: 0,
      y: 0,
    });
  };

  const handleViewAdmin = (userId: string | number) => {
    navigate(`/admins/${userId}`);
    handleCloseContextMenu();
  };

  const handleDeleteAdmin = async (userId: string | number) => {
    await remove(userId);
    setConfirmOpen(null);
    handleCloseContextMenu();
  };

  return (
    <div className="flex-1 h-full rounded-[32px] p-2 id-card-container overflow-y-auto max-lg:my-2.5">
      <div className="px-8 py-6">
        <h1 className="id-card-title font-medium max-lg:!text-2xl max-md:!text-xl">Admins/Managers</h1>

        {error && <p className="text-red-400 text-sm mt-2">{error}</p>}

        <div className="mt-6 bg-white-overlay big-corner p-6 max-md:p-0 max-md:bg-transparent overflow-hidden">
          <div className="flex justify-between items-center mb-6 max-xl:flex-col-reverse max-xl:items-baseline max-xl:gap-4">
            <div className="flex gap-4 w-[40%] max-xl:w-[60%] max-md:w-full">
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-4 py-3 pl-8 h-10 border rounded-lg focus:outline-none focus:ring-2 id-card-input"
                />
                <img src="/icons/Search.svg" alt="search" className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" />
              </div>
              {/* <button onClick={() => loadList()} className="px-6 py-2 rounded-full bg-secondary-bg text-white hover:bg-opacity-90 transition-colors">Search</button> */}
            </div>
             <div className="flex items-center gap-4">
 							<button className="id-card-button-primary px-4 py-2 rounded-full" onClick={() => navigate('/admins/create')}>Add admin user</button>
               {/* <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={usersOnly} onChange={(e) => setUsersOnly(e.target.checked)} /> Users only</label>
               <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={adminsOnly} onChange={(e) => setAdminsOnly(e.target.checked)} /> Admins only</label> */}
             </div>
          </div>
          <table className="w-full text-left">
            <thead>
              <tr className="text-gray-700">
                <th className="px-3 py-2">ID</th>
                <th className="px-3 py-2">First</th>
                <th className="px-3 py-2">Last</th>
                <th className="px-3 py-2">Role</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="text-[var(--gray-800)]">
              {currentData.map((u) => (
                <>
                  <tr key={String(u.id)} className="border-t border-gray-200">
                    <td className="px-3 py-2 text-sm opacity-80 text-[var(--gray-600)]">{String(u.id)}</td>
                    <td className="px-3 py-2">{u.firstName}</td>
                    <td className="px-3 py-2">{u.lastName}</td>
                    <td className="px-3 py-2">{getRoleName(u.role)}</td>
                    <td className="px-3 py-2">{u.status === StatusActiveUser.Pending ? 'Pending' : u.status === StatusActiveUser.Active ? 'Active' : 'Banned'}</td>
                    {currentUser?.role === RoleType.Admin && <td className="px-3 py-2 text-right">
                      <button
                        onClick={e => handleContextMenuClick(e, String(u.id))}
                        className="p-2 py-3 border-2 border-gray-200 hover:bg-gray-100 rounded-full"
                      >
                        <img
                          className="min-w-[10px]"
                          src="/icons/dots.svg"
                          alt="dots"
                        />
                      </button>
                    </td>}
                  </tr>
                </>
              ))}
              {filtered.length === 0 && !loading && (
                <tr><td className="px-3 py-4" colSpan={6}>No users</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex justify-center mt-6 max-w-[1100px]">
          <Pagination>
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
              {Array.from({ length: Math.min(totalPages, 20) }, (_, i) => i + 1).map((page) => (
                <PaginationItem key={page}>
                  <PaginationLink
                    onClick={() => setCurrentPage(page)}
                    isActive={currentPage === page}
                    className="cursor-pointer"
                  >
                    {page}
                  </PaginationLink>
                </PaginationItem>
              ))}
              {totalPages > 20 && (
                <>
                  <PaginationItem>
                    <span className="px-3 py-2">...</span>
                  </PaginationItem>
                  <PaginationItem>
                    <PaginationLink onClick={() => setCurrentPage(totalPages)} className="cursor-pointer">
                      {totalPages}
                    </PaginationLink>
                  </PaginationItem>
                </>
              )}
              <PaginationItem>
                <PaginationNext
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>

        {contextMenu.isOpen && (
          <div
            className="fixed bg-white card-corner shadow-lg border border-gray-200 z-50 w-fit"
            style={{
              left: contextMenu.x,
              top: contextMenu.y,
            }}
          >
            <button
              onClick={() => handleViewAdmin(contextMenu.userId)}
              className="block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition-colors whitespace-nowrap"
            >
              View
            </button>
            {!isCurrentUser(contextMenu.userId) && (
              <button
                onClick={() => setConfirmOpen(contextMenu.userId)}
                className="block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors whitespace-nowrap"
              >
                Delete
              </button>
            )}
          </div>
        )}

        {contextMenu.isOpen && (
          <div className="fixed inset-0 z-40" onClick={handleCloseContextMenu} />
        )}

        <ConfirmPopup
          isOpen={!!confirmOpen}
          onClose={() => setConfirmOpen(null)}
          title="Delete user"
          message="Are you sure you want to delete this user?"
          confirmText="Delete"
          onConfirm={() => { if (confirmOpen != null) handleDeleteAdmin(confirmOpen as any); }}
        />
      </div>
    </div>
  );
};

export default Admins;
